import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { Helmet } from "react-helmet";
import { FaSyncAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;
const BRANCH_LIST_URL = `https://${process.env.REACT_APP_API_KEY_AUTH_BRANCH}.execute-api.ap-south-1.amazonaws.com/default/createBranch`;
const BALANCE_API = `https://${process.env.REACT_APP_API_KEY_AUTH_BALANCE}.execute-api.ap-south-1.amazonaws.com/default/balanceUpdate`;
const PARTY_API_URL = `https://${process.env.REACT_APP_THIRD_PARTY_API_URL}.execute-api.ap-south-1.amazonaws.com/default/thirdPartyUpdate`;

export default function AdminEntry() {
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [allBranches, setAllBranches] = useState([]);
  const [party, setParty] = useState([]);

  // Form States
  const [fromBranch, setFromBranch] = useState("");
  const [selectedType, setSelectedType] = useState("IN");
  const [isToBranch, setIsToBranch] = useState(true);
  const [createLakEntry, setCreateLakEntry] = useState(true);

  // 1. Fetch All Branches & Parties on Load
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resB, resP] = await Promise.all([
        fetch(BRANCH_LIST_URL),
        fetch(PARTY_API_URL, {
          headers: { Authorization: `Bearer ${auth.user?.access_token}` },
        }),
      ]);
      const dataB = await resB.json();
      const dataP = await resP.json();
      setAllBranches(dataB.branches || []);
      setParty(dataP.branches || []);
    } catch (err) {
      console.error("Initialization Error:", err);
    }
    setLoading(false);
  }, [auth.user?.access_token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 2. Balance Sync Helper (Modified for Admin)
  const syncBalance = async (bId, amount, type) => {
    const isInflow = ["IN", "BTIN"].includes(type);
    const action = isInflow ? "ADD" : "REDUCE";

    await fetch(BALANCE_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: bId,
        branchName: bId,
        amount: Number(amount),
        action: action,
      }),
    });
  };

  // 3. Handle Post (The Core Logic)
  const handlePost = async (e) => {
    e.preventDefault();
    if (!fromBranch) return alert("Select a source branch!");

    setLoading(true);
    const fd = new FormData(e.target);
    const baseId = `ADM-${Date.now()}`;
    const amount = parseFloat(fd.get("amount"));
    const commission = parseFloat(fd.get("commission")) || 0;
    const target = fd.get("targetBranch");
    const thirdParty = fd.get("thirdParty");

    let payload = [];

    // Mapping logic for standard IN/OUT/BT
    const pType =
      selectedType === "IN"
        ? "OUT"
        : selectedType === "OUT"
          ? "IN"
          : selectedType === "BTIN"
            ? "BTOUT"
            : "BTIN";

    if (!isToBranch) {
      // 3rd party — mirror BranchHome pattern exactly
      const lakOppositeType = pType;
      payload.push({
        paymentId: `${baseId}-TP`,
        paymentType: selectedType,
        flowRole: "SENDER",
        fromBranch: fromBranch,
        toBranch: thirdParty,
        amount,
        commission,
        status: "DELIVERED",
        remarks: `[TP: ${thirdParty}] ${fd.get("remarks")}`,
        timestamp: new Date().toISOString(),
        linkedId: createLakEntry ? `${baseId}-LAK` : null,
        thirdParty: thirdParty,
      });
      if (createLakEntry) {
        payload.push({
          paymentId: `${baseId}-LAK`,
          paymentType: lakOppositeType,
          flowRole: "SENDER",
          fromBranch: "LAK",
          toBranch: fromBranch,
          amount,
          commission: 0,
          status: "DELIVERED",
          remarks: `[LAK-TP: ${thirdParty}] ${fd.get("remarks")}`,
          timestamp: new Date().toISOString(),
          linkedId: `${baseId}-TP`,
          thirdParty: thirdParty,
        });
      }
    } else {
      // Branch-to-branch or self
      const txM = {
        paymentId: `${baseId}-${selectedType}`,
        paymentType: selectedType,
        flowRole: "SENDER",
        fromBranch: fromBranch,
        toBranch: target || fromBranch,
        amount,
        commission,
        status: target ? "PENDING" : "DELIVERED",
        remarks: target ? `[ADMIN POST] ${fd.get("remarks")}` : `[ADMIN POST SELF] ${fd.get("remarks")}`,
        timestamp: new Date().toISOString(),
        linkedId: target ? `${baseId}-${pType}` : null,
        thirdParty: null,
      };
      payload.push(txM);
      if (target) {
        payload.push({
          ...txM,
          paymentId: txM.linkedId,
          paymentType: pType,
          flowRole: "RECEIVER",
          commission: 0,
          linkedId: txM.paymentId,
        });
      }
    }

    try {
      await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transactions: payload }),
      });

      // Update the balance of the branch the Admin is acting as
      await syncBalance(fromBranch, amount, selectedType);
      // Sync LAK's balance for the counter entry
      if (!isToBranch && createLakEntry) {
        await syncBalance("LAK", amount, pType);
      }
      if (commission > 0 && isToBranch) {
        await fetch(BALANCE_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: fromBranch,
            branchName: fromBranch,
            amount: Number(commission),
            action: "ADD", // Commission is always an addition to the sender's cash
          }),
        });
      }
      alert("Admin Transaction Posted Successfully");
      e.target.reset();
    } catch (err) {
      alert("Post Failed");
    }
    setLoading(false);
  };

  return (
    <div className="branch-home">
      <Helmet>
        <title>Admin | New Transaction</title>
      </Helmet>

      <header className="branch-header-bar">
        <h2>Admin Portal: New Transaction</h2>
        <button onClick={fetchData} className="br-btn br-btn-primary">
          <FaSyncAlt className={loading ? "spin" : ""} /> Refresh Data
        </button>
        <Link to="/">
          <button className="br-btn">Admin Dashboard</button>
        </Link>
      </header>

      <section className="branch-section">
        <div style={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
          <button
            type="button"
            onClick={() => setIsToBranch(true)}
            className={`br-btn ${isToBranch ? "br-btn-primary" : ""}`}
          >
            To Branch
          </button>
          <button
            type="button"
            onClick={() => setIsToBranch(false)}
            className={`br-btn ${!isToBranch ? "br-btn-primary" : ""}`}
          >
            To 3rd Party
          </button>
        </div>

        <form onSubmit={handlePost} className="branch-form-grid">
          {/* FROM BRANCH SELECTOR (NEW FOR ADMIN) */}
          <div className="branch-form-row">
            {/* <label>Posting From:</label> */}
            <select
              value={fromBranch}
              onChange={(e) => setFromBranch(e.target.value)}
              required
            >
              <option value="">-- Select From Branch --</option>
              {allBranches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.id}
                </option>
              ))}
            </select>
          </div>

          <div className="branch-form-row">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="IN">IN</option>
              <option value="OUT">OUT</option>
              <option value="BTIN">BTIN</option>
              <option value="BTOUT">BTOUT</option>
            </select>

            {isToBranch ? (
              <select name="targetBranch">
                <option value="">-- Select To Branch --</option>
                {allBranches
                  .filter((b) => b.id !== fromBranch)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.id}
                    </option>
                  ))}
              </select>
            ) : (
              <select name="thirdParty" required>
                <option value="">-- 3rd Party --</option>
                {party.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.partyName}
                  </option>
                ))}
              </select>
            )}
          </div>

          {!isToBranch && (
            <div className="branch-form-row" style={{ alignItems: "center", gap: "10px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={createLakEntry}
                  onChange={(e) => setCreateLakEntry(e.target.checked)}
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
                Create opposite entry for LAK
                <span style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 400 }}>
                  (LAK gets {selectedType === "IN" || selectedType === "BTIN" ? "opposite OUT" : "opposite IN"})
                </span>
              </label>
            </div>
          )}

          <div className="branch-form-row">
            <input name="amount" type="number" placeholder="Amount" required />
            <input name="commission" type="number" placeholder="Commission" />
            <input name="remarks" placeholder="Admin Remarks..." required />
          </div>

          <button
            type="submit"
            className="post-btn"
            disabled={loading || !fromBranch}
          >
            {loading ? "Processing..." : "Execute Admin Post"}
          </button>
        </form>
      </section>
    </div>
  );
}
